import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faUndo, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import '../styles/Trash.css';
import useI18n from '../hooks/useI18n';

interface TrashItem {
  id: string;
  name: string;
  description?: string;
  deletedAt?: Date;
  category?: {
    id: string;
    name: string;
  };
  imageUrl?: string;
  purchasePrice?: number;
}

const Trash = () => {
  const [trashItems, setTrashItems] = useState<TrashItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();

  useEffect(() => {
    fetchTrashItems();
  }, []);

  const fetchTrashItems = async () => {
    try {
      setLoading(true);
      const response = await api.get('/items?includeDeleted=true&take=100');
      const deletedItems = response.data.items.filter((item: any) => item.deletedAt !== null);
      setTrashItems(deletedItems);
    } catch (error) {
      console.error('Failed to fetch trash items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await api.post(`/items/${id}/restore`);
      setTrashItems(trashItems.filter(item => item.id !== id));
    } catch (error) {
      console.error('Failed to restore item:', error);
    }
  };

  const handlePermanentDelete = async (id: string) => {
    if (!window.confirm(t('trash.confirmDelete'))) {
      return;
    }
    try {
      await api.delete(`/items/${id}/permanent`);
      setTrashItems(trashItems.filter(item => item.id !== id));
    } catch (error) {
      console.error('Failed to permanently delete item:', error);
    }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm(t('trash.confirmEmpty'))) {
      return;
    }
    try {
      await Promise.all(
        trashItems.map(item => api.delete(`/items/${item.id}/permanent`))
      );
      setTrashItems([]);
    } catch (error) {
      console.error('Failed to empty trash:', error);
    }
  };

  const getDaysUntilDeletion = (deletedAt?: Date) => {
    if (!deletedAt) return 30;
    const deleted = new Date(deletedAt);
    const now = new Date();
    const diffTime = 30 * 24 * 60 * 60 * 1000 - (now.getTime() - deleted.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getDeletedTimeAgo = (deletedAt?: Date) => {
    if (!deletedAt) return t('trash.unknown');
    const deleted = new Date(deletedAt);
    const now = new Date();
    const diffMs = now.getTime() - deleted.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffDays > 0) return t('trash.daysAgo').replace('{count}', String(diffDays));
    if (diffHours > 0) return t('trash.hoursAgo').replace('{count}', String(diffHours));
    return t('trash.justNow');
  };

  if (loading) {
    return <div className="trash-loading">{t('trash.loading')}</div>;
  }

  return (
    <div className="trash">
      <div className="trash-header">
        <h2>{t('trash.title')}</h2>
        <div className="trash-stats">
          <span className="item-count">{t('trash.items').replace('{count}', String(trashItems.length))}</span>
          {trashItems.length > 0 && (
            <button className="btn-empty-trash" onClick={handleEmptyTrash}>
              {t('trash.emptyTrash')}
            </button>
          )}
        </div>
      </div>

      {trashItems.length > 0 && (
        <div className="trash-warning">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <p>{t('trash.warning')}</p>
        </div>
      )}

      {trashItems.length > 0 ? (
        <div className="trash-list">
          {trashItems.map(item => (
            <div key={item.id} className="trash-item">
              {item.imageUrl && (
                <div className="trash-item-image">
                  <img 
                    src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${item.imageUrl}`} 
                    alt={item.name}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="trash-item-info">
                <div className="item-details">
                  <h4>{item.name}</h4>
                  {item.description && (
                    <p className="item-description">{item.description}</p>
                  )}
                  <div className="item-meta">
                    <span className="deleted-date">{t('trash.deleted')} {getDeletedTimeAgo(item.deletedAt)}</span>
                    {item.category && (
                      <span className="category-badge">{item.category.name}</span>
                    )}
                    {item.purchasePrice && (
                      <span className="item-price">${item.purchasePrice.toFixed(2)}</span>
                    )}
                  </div>
                </div>
                <div className="expiration-info">
                  <span className="expires-label">{t('trash.expiresIn')}</span>
                  <span className="expires-time">{getDaysUntilDeletion(item.deletedAt)} {t('trash.days')}</span>
                </div>
              </div>
              <div className="trash-item-actions">
                <button 
                  className="btn-restore"
                  onClick={() => handleRestore(item.id)}
                  title="Restore item"
                >
                  <FontAwesomeIcon icon={faUndo} />
                  {t('trash.restore')}
                </button>
                <button 
                  className="btn-delete"
                  onClick={() => handlePermanentDelete(item.id)}
                  title="Delete permanently"
                >
                  <FontAwesomeIcon icon={faTrash} />
                  {t('trash.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <FontAwesomeIcon icon={faTrash} className="empty-icon" />
          <h3>{t('trash.emptyTitle')}</h3>
          <p>{t('trash.emptyDesc')}</p>
        </div>
      )}
    </div>
  );
};

export default Trash;
